import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  Box,
  Alert,
  IconButton,
  TextField,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useApi } from "../../utils/api"; // Import useApi hook
import VideoPlayerModal from "../Shared/VideoPlayerModal"; // Import the player modal

function VideoSelectionModal({ open, onClose, onSelectVideo }) {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState("");
  const { getContentMetadata } = useApi(); // Get the API function

  // State for video player modal
  const [playerOpen, setPlayerOpen] = useState(false);
  const [playingVideoKey, setPlayingVideoKey] = useState(null);
  const [playingVideoName, setPlayingVideoName] = useState(null);

  // Function to fetch videos from content-service
  const fetchVideos = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Use the actual API call
      const data = await getContentMetadata();
      setVideos(data || []); // Ensure data is an array
      if (!Array.isArray(data)) {
        console.warn("getContentMetadata did not return an array:", data);
        setVideos([]);
      }
    } catch (err) {
      setError(err.message || "Failed to load videos");
      console.error("Error fetching videos for modal:", err);
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch videos when the modal opens
  useEffect(() => {
    if (open) {
      fetchVideos();
    }
  }, [open]); // Rerun when open changes

  const handleVideoClick = (video) => {
    if (video && video.videoKey) {
      onSelectVideo(video.videoKey); // Pass the selected video URL back
    }
    onClose(); // Close the modal after selection
  };

  // Function to open the video player
  const handlePlayVideo = (video) => {
    if (video && video.videoKey) {
      setPlayingVideoKey(video.videoKey);
      setPlayingVideoName(video.name);
      setPlayerOpen(true);
    }
  };

  const handleClosePlayer = () => {
    setPlayerOpen(false);
    setPlayingVideoKey(null);
    setPlayingVideoName(null);
  };

  // Filter videos by name
  const filteredVideos = videos.filter((video) =>
    video.name?.toLowerCase().includes(filterText.toLowerCase())
  );

  // Reset filter when modal closes
  useEffect(() => {
    if (!open) {
      setFilterText("");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Video</DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            Error loading videos: {error}
          </Alert>
        )}
        {!isLoading && !error && (
          <>
            <TextField
              fullWidth
              label="Filter videos by name"
              variant="outlined"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              sx={{ mb: 2 }}
              autoFocus
            />
            <List>
              {filteredVideos.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary={
                      filterText
                        ? `No videos found matching "${filterText}".`
                        : "No videos found in your library."
                    }
                  />
                </ListItem>
              )}
              {filteredVideos.map((video) => (
                <ListItem
                  key={video.contentId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    py: 2,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  onClick={() => handleVideoClick(video)}
                >
                  {/* Thumbnail */}
                  <Box
                    sx={{
                      width: 120,
                      height: 68,
                      backgroundColor: "grey.200",
                      borderRadius: 1,
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {video.thumbnailKey && (
                      <img
                        src={`https://trainer-app-videos.s3.us-east-2.amazonaws.com/${video.thumbnailKey}`}
                        alt={video.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </Box>

                  {/* Video Info */}
                  <Box sx={{ flex: 1 }}>
                    <ListItemText
                      primary={video.name}
                      secondary={`Tags: ${video.tags?.join(", ") || "None"}`}
                    />
                  </Box>

                  {/* Preview Button */}
                  <IconButton
                    edge="end"
                    aria-label="preview"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlayVideo(video);
                    }}
                  >
                    <PlayCircleOutlineIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      {/* Video Player Modal */}
      <VideoPlayerModal
        open={playerOpen}
        onClose={handleClosePlayer}
        videoKey={playingVideoKey}
        videoName={playingVideoName}
      />
    </Dialog>
  );
}

export default VideoSelectionModal;
